import { WorklistQueryParams } from '@/utils/urlQueries'
import DateFilterComponent, { DateRange } from '@components/DateFilterComponent'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import {
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  debounce,
  Dialog,
  DialogContent,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
} from '@mui/x-data-grid-pro'
import { PatientDto, Site } from '@services/api'
import { PatientServiceV2 } from '@services/PatientService'
import { doFindOrders, setOrderFilters } from '@state/reducers/orderReducer'
import { ReceiptText, RefreshCcw, UserRoundSearch, X } from 'lucide-react'
import moment from 'moment'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import DoctorList from '../../../containers/app/parameters/doctors/DoctorList'
import { OrderFilterTabs } from './OrderFilterTabs'
import { OrderFilterTab } from '@services/MemberPreferenceConfigService'

type OrderToolbarProps = {
  onInstantBillClick: () => void
  onRefresh: () => void
  urlState: WorklistQueryParams | null
  updateParam: (key: keyof WorklistQueryParams, value: any) => void
  updateParams: (params: Partial<WorklistQueryParams>) => void
  clearParams: () => void
}

export const OrderToolbar: React.FC<OrderToolbarProps> = ({
  onInstantBillClick,
  onRefresh,
  urlState,
  updateParam,
  updateParams,
}) => {
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const [isDoctorsOpen, setisDoctorsOpen] = useState(false)
  const updateParamRef = useRef(updateParam)

  const filters = useAppSelector(({ order }) => order.filters)
  const user = useAppSelector(({ auth }) => auth.user)

  const [selectedDateRange, setselectedDateRange] = useState<DateRange | null>(
    filters.startDate && filters.endDate
      ? {
          startDate: moment(filters.startDate).toDate(),
          endDate: moment(filters.endDate).toDate(),
        }
      : null,
  )

  // Patient multiselect state
  const [selectedPatients, setSelectedPatients] = useState<PatientDto[]>([])
  const [patientOptions, setPatientOptions] = useState<PatientDto[]>([])
  const [patientSearchLoading, setPatientSearchLoading] = useState(false)
  const [patientInputValue, setPatientInputValue] = useState('')

  useEffect(() => {
    if (urlState?.startDate && urlState?.endDate) {
      setselectedDateRange({
        startDate: moment(urlState.startDate).toDate(),
        endDate: moment(urlState.endDate).toDate(),
      })
    }
  }, [urlState?.startDate, urlState?.endDate])

  // Restore selected patients from URL on mount
  useEffect(() => {
    if (!urlState?.patientIds || selectedPatients.length > 0) {
      return
    }

    const ids = urlState.patientIds

    if (ids.length === 0) {
      return
    }

    PatientServiceV2.findMany({ patientIds: ids, limit: ids.length })
      .then((res) => {
        setSelectedPatients(res.datas || [])
      })
      .catch(() => {})
  }, [urlState?.patientIds])

  // Debounced patient search
  const searchPatients = useMemo(
    () =>
      debounce((value: string) => {
        if (!value || value.length < 2) {
          setPatientOptions([])
          setPatientSearchLoading(false)
          return
        }
        setPatientSearchLoading(true)
        PatientServiceV2.findMany({ search: value, limit: 10 })
          .then((res) => {
            setPatientOptions(res.datas || [])
          })
          .catch(() => {
            setPatientOptions([])
          })
          .finally(() => {
            setPatientSearchLoading(false)
          })
      }, 300),
    [],
  )

  useEffect(() => {
    return () => searchPatients.clear()
  }, [searchPatients])

  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh()
    } else {
      dispatch(doFindOrders())
    }
  }

  useEffect(() => {
    updateParamRef.current = updateParam
  }, [updateParam])

  const onSiteChange = (ev: SelectChangeEvent<number>) => {
    const siteId = ev.target.value
    updateParam('siteId', siteId || null)
  }

  const sites = useMemo(() => {
    return (user as any & { sites: Site[] })?.sites
  }, [user])

  useEffect(() => {
    if (sites?.length === 1) {
      dispatch(
        setOrderFilters({
          siteId: sites[0].id,
        }),
      )
    }
  }, [sites])

  const handleDateRangeChange = (dateRange: DateRange) => {
    setselectedDateRange(dateRange)
    updateParams({
      startDate: dateRange.startDate.getTime(),
      endDate: dateRange.endDate.getTime(),
    })
  }

  const hasPatientFilter = selectedPatients.length > 0

  const savedOrderFilters = useAppSelector(
    ({ memberPreferenceConfig }) =>
      memberPreferenceConfig.config?.orderFilters as OrderFilterTab[] | null,
  )

  const activeTabId = useMemo(() => {
    if (filters.hasRadiologyStudy) {
      return (
        savedOrderFilters?.find((t) => t.type === 'radiologyStudy')?.id ?? null
      )
    }
    const status = urlState?.state ?? filters.status
    if (status) {
      return status as string
    }
    return null
  }, [
    filters.hasRadiologyStudy,
    urlState?.state,
    savedOrderFilters,
    filters.status,
  ])

  const handlePatientSelectionChange = (_: unknown, newValue: PatientDto[]) => {
    setSelectedPatients(newValue)
    if (newValue.length > 0) {
      const ids = newValue.map((p) => p.id)
      updateParams({
        patientIds: ids,
        search: null,
        startDate: null,
        endDate: null,
      })
      setselectedDateRange(null)
    } else {
      const todayStart = moment().startOf('day').valueOf()
      const todayEnd = moment().endOf('day').valueOf()
      updateParams({
        patientIds: null,
        startDate: todayStart,
        endDate: todayEnd,
      })
    }
  }

  return (
    <>
      <GridToolbarContainer className="flex items-center gap-3 mb-3">
        <Tooltip title={intl.formatMessage({ id: 'refresh' })}>
          <IconButton onClick={handleRefresh}>
            <RefreshCcw />
          </IconButton>
        </Tooltip>
        <GridToolbarColumnsButton
          sx={{
            backgroundColor: '#3f3f3f',
            color: '#fff',
            marginRight: '10px',
            '&:hover': {
              backgroundColor: '#3f3f3f70',
            },
          }}
          onResize={undefined}
          onResizeCapture={undefined}
        />
        <GridToolbarDensitySelector
          sx={{
            backgroundColor: '#3f3f3f',
            color: '#fff',
            '&:hover': {
              backgroundColor: '#3f3f3f70',
            },
          }}
          onResize={undefined}
          onResizeCapture={undefined}
        />
        {/* <GridToolbarExport /> */}

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="site-select-label">Site</InputLabel>
          <Select
            labelId="site-select-label"
            label="Site"
            value={urlState?.siteId ?? filters.siteId ?? ''}
            onChange={onSiteChange}
          >
            {sites.map((site: Site) => (
              <MenuItem key={site.id} value={site.id}>
                {site.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Autocomplete
          multiple
          size="small"
          options={patientOptions}
          value={selectedPatients}
          onChange={handlePatientSelectionChange}
          inputValue={patientInputValue}
          onInputChange={(_, value, reason) => {
            setPatientInputValue(value)
            if (reason === 'input') {
              searchPatients(value)
            }
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          filterOptions={(x) => x}
          loading={patientSearchLoading}
          noOptionsText={
            patientInputValue.length < 2
              ? 'Tapez au moins 2 caractères'
              : 'Aucun patient trouvé'
          }
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.id}
                // className="!rounded-full !ml-1.5 !h-"
                label={
                  <div className="flex flex-col">
                    <Typography variant="body2" fontWeight={500}>
                      {option.firstName} {option.lastName}
                    </Typography>
                    <Typography
                      variant="caption"
                      fontSize="0.7rem"
                      lineHeight="1"
                    >
                      {option.ssn}
                    </Typography>
                  </div>
                }
                size="medium"
              />
            ))
          }
          renderOption={(props, option) => (
            <li
              {...props}
              key={option.id}
              className="p-2.5 flex items-start justify-between hover:bg-gray-50 hover:cursor-pointer"
            >
              <Typography variant="body2" fontWeight={500}>
                {option.firstName} {option.lastName}
              </Typography>
              {option.birthDateStr && (
                <Typography variant="caption" color="text.secondary">
                  Né(e) le {option.birthDateStr}
                </Typography>
              )}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={
                selectedPatients.length === 0
                  ? intl.formatMessage({
                      id: 'searchPatient',
                      defaultMessage: 'Rechercher un patient',
                    })
                  : ''
              }
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {patientSearchLoading ? (
                      <CircularProgress color="inherit" size={18} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          sx={{
            minWidth: 400,
            backgroundColor: '#fff',
            borderRadius: '5px',
            '& .MuiOutlinedInput-root': {
              minHeight: '40px',
            },
          }}
        />
        <DateFilterComponent
          dateRange={selectedDateRange}
          onDateRangeChange={handleDateRangeChange}
          disabled={!!filters.search || hasPatientFilter}
        />

        {user?.id && (
          <OrderFilterTabs
            memberId={user.id}
            updateParam={updateParam}
            activeValue={activeTabId}
          />
        )}
        <Tooltip title="Rechercher un médecin">
          <Button
            color="primary"
            variant="contained"
            onClick={() => setisDoctorsOpen(true)}
          >
            <UserRoundSearch />
          </Button>
        </Tooltip>

        <Tooltip title="Facturer un patient">
          <Button
            onClick={onInstantBillClick}
            variant="contained"
            color="primary"
          >
            <ReceiptText />
          </Button>
        </Tooltip>
      </GridToolbarContainer>
      <Dialog
        fullWidth
        maxWidth="xl"
        open={isDoctorsOpen}
        onClose={() => setisDoctorsOpen(false)}
      >
        <DialogContent className="flex flex-col gap-2 h-screen">
          <IconButton
            size="small"
            color="error"
            onClick={() => setisDoctorsOpen(false)}
            className="self-end"
          >
            <X />
          </IconButton>
          <DoctorList />
        </DialogContent>
      </Dialog>
    </>
  )
}
